import React, { useMemo } from "react";
import "./SpaceCard.scss";
import type { Space, SpaceContact } from "@/types";
import { Button, trackEvent } from "@/components/atoms";
import { MapPin } from "lucide-react";
import {
  getSpaceTenantSiteUrl,
  redirectTo,
  withImageKitTransform,
} from "@/utils";
import {
  buildSocialLinksFromContact,
  normalizeSocialHandle,
} from "@/utils/socialLinks";
import { SocialLinks } from "../SocialLinks";

interface SpaceCardProps {
  space: Space;
}

type ContactDisplayKey = Exclude<keyof SpaceContact, "maps" | "externalLink">;

const CONTACT_DISPLAY_FIELDS: readonly {
  key: ContactDisplayKey;
  label: string;
  format: "plain" | "handle";
}[] = [
  { key: "email", label: "Email:", format: "plain" },
  { key: "whatsapp", label: "WPP:", format: "plain" },
  { key: "phoneForCalls", label: "TEL:", format: "plain" },
  { key: "instagram", label: "IG:", format: "handle" },
  { key: "facebook", label: "FB:", format: "handle" },
  { key: "tiktok", label: "TikTok:", format: "handle" },
  { key: "x", label: "X:", format: "handle" },
  { key: "youtube", label: "YT:", format: "handle" },
];

const formatContactDisplayValue = (
  format: "plain" | "handle",
  value: string,
): string =>
  format === "handle" ? `@${normalizeSocialHandle(value)}` : value.trim();

const buildContactDisplayRows = (
  contact: SpaceContact,
): { key: ContactDisplayKey; label: string; value: string }[] =>
  CONTACT_DISPLAY_FIELDS.flatMap(({ key, label, format }) => {
    const raw = contact[key];
    if (!raw?.trim()) {
      return [];
    }

    return [{ key, label, value: formatContactDisplayValue(format, raw) }];
  });

/**
 * Card component to display information about a location/theater room
 */
export const SpaceCard: React.FC<SpaceCardProps> = ({ space }) => {
  const contactRows = useMemo(
    () => buildContactDisplayRows(space.contact),
    [space.contact],
  );

  const socialLinks = useMemo(
    () =>
      buildSocialLinksFromContact(space.contact, space.name).filter(
        (link) => link.icon !== "maps",
      ),
    [space.contact, space.name],
  );

  const handleMapLinkClick = (url: string) => {
    trackEvent("User Interaction", "click_space_map-link", space.name);
    redirectTo(url);
  };

  const handleSocialClick = () => {
    trackEvent("User Interaction", "click_space_social", space.name);
  };

  return (
    <div className="space-card">
      <div className="space-card__overlay" />

      <div className="space-card__content">
        {space.logo && (
          <div className="space-card__logo-container">
            <img
              src={withImageKitTransform(space.logo, "spaceCard")}
              alt={space.name}
              className="space-card__logo"
            />
          </div>
        )}

        <h2 className="space-card__name">{space.name}</h2>

        <p
          className={
            space.contact.maps
              ? "space-card__address space-card__address--link"
              : "space-card__address"
          }
          onClick={
            space.contact.maps
              ? () => handleMapLinkClick(space.contact.maps!)
              : void 0
          }
        >
          <MapPin className="space-card__address-icon" />
          {space.address}
        </p>
        <div className="space-card__alignment" aria-hidden="true" />

        {contactRows.length > 0 ? (
          <div className="space-card__contact-info">
            {contactRows.slice(0, 4).map(({ key, label, value }) => (
              <p key={key} className="space-card__contact-element">
                <span className="space-card__label">{label}</span> {value}
              </p>
            ))}
          </div>
        ) : null}

        <div className="space-card__alignment" aria-hidden="true" />

        <div className="space-card__tenant-cta" style={{display: "none"}}> {/* TODO: Remove this once the space card is updated */}
          {space.tenantSlug ? (
            <Button
              variant="primary"
              appearance="solid"
              fullWidth
              density="default"
              motionScale="base"
              href={getSpaceTenantSiteUrl(space.tenantSlug)}
              actionTrackSufix="space-card_tenant-site"
            >
              Visitar Espacio Digital
            </Button>
          ) : (
            <Button
              variant="secondary"
              appearance="solid"
              fullWidth
              density="default"
              disabled
              ariaLabel="Espacio digital en Tercer Espacio próximamente"
            >
              Espacio Digital pendiente
            </Button>
          )}
        </div>
        <SocialLinks
          links={socialLinks}
          onLinkClick={handleSocialClick}
          variant="secondary"
          limit={4}
        />
      </div>
    </div>
  );
};

export default SpaceCard;
